/**
 * Client-side stale-while-revalidate cache.
 *
 * Solves the "every page click flashes Loading…" problem by:
 *
 *   1. Returning the **last response we've seen for this key**
 *      synchronously, so the page renders content immediately on
 *      remount instead of showing a spinner.
 *   2. Kicking off a background refetch in parallel — when fresh
 *      data arrives the consumer's setter is called again with the
 *      new payload.
 *
 * Persistence:
 *   • In-memory Map → fastest, survives page navigation within the SPA.
 *   • sessionStorage (mirror) → survives full-page reload AND back-button
 *     navigation. Cleared automatically on tab close.
 *
 * Per-user isolation:
 *   • Cache keys are namespaced by the JWT-derived user id at write time
 *     so logging out as user A and in as user B never bleeds data.
 *
 * TTL:
 *   • The cache always returns whatever it has on disk, even if "stale".
 *     The consumer pages re-render with fresh data when the background
 *     fetch completes. So TTL is mostly a hint for the UI to show a
 *     small "refreshing" indicator vs nothing.
 */

const STORAGE_PREFIX = 'hala:cc:';
const VERSION = 1;
const DEFAULT_TTL_MS = 5 * 60 * 1000; // 5 min

const memoryStore = new Map<string, { time: number; data: any; userKey: string }>();

function userKey(): string {
  // AuthContext stores the user under the `user` localStorage key.
  try {
    const raw = window.localStorage.getItem('user');
    if (!raw) return 'anon';
    const obj = JSON.parse(raw);
    return String(obj?.id ?? obj?.username ?? 'anon');
  } catch {
    return 'anon';
  }
}

function buildKey(scope: string, params?: Record<string, any>): string {
  const u = userKey();
  if (!params || Object.keys(params).length === 0) return `${u}::${scope}`;
  const sortedParams = Object.keys(params)
    .sort()
    .map(k => `${k}=${String(params[k] ?? '')}`)
    .join('&');
  return `${u}::${scope}::${sortedParams}`;
}

function readSessionStorage(key: string): { time: number; data: any; userKey: string } | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== VERSION) return null;
    return parsed.entry;
  } catch {
    return null;
  }
}

function writeSessionStorage(key: string, entry: { time: number; data: any; userKey: string }): void {
  try {
    window.sessionStorage.setItem(
      STORAGE_PREFIX + key,
      JSON.stringify({ v: VERSION, entry })
    );
  } catch {
    /* QuotaExceeded or storage disabled — silently skip the persistence layer. */
  }
}

/**
 * Read the last cached value for `scope` + `params`.
 * Returns null if nothing's been stored.
 */
export function getCached<T = any>(scope: string, params?: Record<string, any>): T | null {
  const key = buildKey(scope, params);
  const u = userKey();
  let entry = memoryStore.get(key);
  if (!entry) {
    const fromStorage = readSessionStorage(key);
    if (fromStorage && fromStorage.userKey === u) {
      memoryStore.set(key, fromStorage);
      entry = fromStorage;
    }
  }
  if (!entry || entry.userKey !== u) return null;
  return entry.data as T;
}

/**
 * Store a value in both memory and sessionStorage.
 */
export function setCached<T = any>(scope: string, params: Record<string, any> | undefined, data: T): void {
  const key = buildKey(scope, params);
  const entry = { time: Date.now(), data, userKey: userKey() };
  memoryStore.set(key, entry);
  writeSessionStorage(key, entry);
}

/**
 * Get the "age" of a cached entry in milliseconds. Useful for showing a
 * "Refreshed N seconds ago" pill or deciding whether to skip a revalidation.
 */
export function cacheAgeMs(scope: string, params?: Record<string, any>): number | null {
  const key = buildKey(scope, params);
  const u = userKey();
  let entry = memoryStore.get(key);
  if (!entry) {
    const fromStorage = readSessionStorage(key);
    if (fromStorage && fromStorage.userKey === u) entry = fromStorage;
  }
  if (!entry || entry.userKey !== u) return null;
  return Date.now() - entry.time;
}

/**
 * Stale-while-revalidate fetch.
 *
 *   const { data, isStale, isRefreshing } = await swrFetch(
 *     'inbound:bundle:90d',
 *     { date_from, date_to },
 *     () => api.getInboundBundle({ date_from, date_to }),
 *     { onUpdate: setData },   // called again when fresh data arrives
 *   );
 *
 * The function returns synchronously-cached data first (if any) AND in
 * the background fetches fresh data, calling `onUpdate(fresh)` when it
 * arrives. Consumers should treat `data` as the immediately-renderable
 * payload.
 */
export interface SwrOpts<T> {
  /** Called when fresh data arrives. Use this to update component state. */
  onUpdate: (fresh: T) => void;
  /** Called if the background fetch fails. Default: silent. */
  onError?: (err: any) => void;
  /** TTL hint for the UI in ms. Default 5 min. */
  ttlMs?: number;
}

export async function swrFetch<T = any>(
  scope: string,
  params: Record<string, any> | undefined,
  fetcher: () => Promise<T>,
  opts: SwrOpts<T>,
): Promise<{ data: T | null; isStale: boolean; isRefreshing: boolean }> {
  const cached = getCached<T>(scope, params);
  const ttl = opts.ttlMs ?? DEFAULT_TTL_MS;
  const age = cacheAgeMs(scope, params);
  const isStale = age !== null && age > ttl;

  // Fire the network call asynchronously regardless of whether we have a hit.
  (async () => {
    try {
      const fresh = await fetcher();
      setCached<T>(scope, params, fresh);
      opts.onUpdate(fresh);
    } catch (err) {
      opts.onError?.(err);
    }
  })();

  return {
    data: cached,
    isStale,
    isRefreshing: cached !== null,  // only "refreshing" if we already have something on screen
  };
}

/**
 * Invalidate one or all entries. Call after mutations or on logout.
 */
export function invalidateCache(scope?: string): void {
  if (!scope) {
    memoryStore.clear();
    try {
      const toRemove: string[] = [];
      for (let i = 0; i < window.sessionStorage.length; i++) {
        const k = window.sessionStorage.key(i);
        if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k);
      }
      toRemove.forEach(k => window.sessionStorage.removeItem(k));
    } catch { /* noop */ }
    return;
  }
  const u = userKey();
  const prefix = `${u}::${scope}`;
  for (const key of Array.from(memoryStore.keys())) {
    if (key.startsWith(prefix)) memoryStore.delete(key);
  }
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const k = window.sessionStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX + prefix)) toRemove.push(k);
    }
    toRemove.forEach(k => window.sessionStorage.removeItem(k));
  } catch { /* noop */ }
}
